import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Stack,
  Skeleton,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Person as PersonIcon,
  LocalShipping as TruckIcon,
  History as HistoryIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import PageHeader from '../../components/PageHeader';
import StatusChip from '../../components/StatusChip';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { useOrganizationStore } from '../../stores/organization.store';
import * as transportationService from '../../services/transportation.service';
import type { DriverDetails } from '../../types/transportation.type';

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'PPp');
  } catch {
    return dateStr;
  }
}

const DriverDetail: React.FC = () => {
  const { t } = useTranslation();
  const { driverId } = useParams<{ driverId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canMutate } = useAuthPermissions();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const [detail, setDetail] = useState<DriverDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgId = selectedOrganization?.id;
  const canEditDriver = Boolean(orgId && canMutate(`recyclee.${orgId}.*.user.write`));

  const fetchDetail = React.useCallback(async () => {
    if (!driverId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await transportationService.getDriverDetails(driverId);
      const raw = response as { result?: DriverDetails } | null;
      const data = raw?.result ?? null;
      setDetail(data);
    } catch (err) {
      const axiosErr = err as { response?: { status?: number; data?: { detail?: string } } };
      const message =
        axiosErr.response?.status === 404
          ? axiosErr.response?.data?.detail ?? 'Driver not found'
          : err instanceof Error
            ? err.message
            : 'Failed to fetch driver details';
      setError(message);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Skeleton variant="rounded" width={280} height={120} />
          <Skeleton variant="rounded" width={280} height={120} />
        </Stack>
        <Skeleton variant="rounded" height={320} />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Box>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/transportation/drivers')}
          sx={{ mb: 2 }}
        >
          {t('transportation.driverDetail.backToDrivers')}
        </Button>
        <Alert
          severity="error"
          onClose={() => setError(null)}
          action={
            <Button color="inherit" size="small" onClick={() => fetchDetail()}>
              {t('common.retry')}
            </Button>
          }
        >
          {error || 'Driver not found.'}
        </Alert>
      </Box>
    );
  }

  const { driver, truck, historical_orders } = detail;
  const iconColor = theme.palette.text.secondary;

  return (
    <Box>
      {/* Driver profile header */}
      <PageHeader
        icon={<PersonIcon />}
        title={driver.username}
        subtitle={t('transportation.driverDetail.subtitle')}
        onBack={() => navigate('/transportation/drivers')}
        backLabel={t('transportation.driverDetail.backToDrivers')}
        actions={
          canEditDriver ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/transportation/drivers`)}
          >
            {t('transportation.driverDetail.edit')}
          </Button>
          ) : undefined
        }
      />

      {/* Driver info cards */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          Driver information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
          <Card
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: theme.palette.divider,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <PersonIcon sx={{ color: iconColor, fontSize: 28 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Username
              </Typography>
              <Typography variant="body1" fontWeight={600} noWrap>
                {driver.username}
              </Typography>
            </Box>
          </Card>
          <Card
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: theme.palette.divider,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <PhoneIcon sx={{ color: iconColor, fontSize: 28 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Phone
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {driver.phone ?? '—'}
              </Typography>
            </Box>
          </Card>
        </Box>
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Driver ID
          </Typography>
          <Typography variant="body2">{driver.id}</Typography>
        </Box>
      </Box>

      {/* Assigned truck */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TruckIcon color="action" fontSize="small" />
          Assigned truck
        </Stack>
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {truck ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {truck.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Capacity: {truck.capacity} kg
                </Typography>
              </Box>
              <StatusChip
                value={truck.status}
                label={t(`transportation.fleet.status.${truck.status}`, { defaultValue: truck.status })}
              />
            </Box>
          ) : (
            <Typography color="text.secondary">No truck assigned</Typography>
          )}
        </CardContent>
      </Card>

      {/* Historical orders */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HistoryIcon color="action" fontSize="small" />
          Completed deliveries
        </Stack>
      </Typography>
      {historical_orders.length === 0 ? (
        <Card sx={{ py: 4, textAlign: 'center', mb: 3 }}>
          <Typography color="text.secondary">
            No completed deliveries for this driver.
          </Typography>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 420 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Delivery</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Completed at</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Waste type</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Amount (kg)
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Recycler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historical_orders.map((row) => (
                <TableRow
                  key={row.delivery_id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:last-of-type td, &:last-of-type th': { border: 0 },
                    '& td, & th': { py: 2.5 },
                  }}
                  onClick={() => navigate(`/deliveries/${row.delivery_id}`)}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ textDecoration: 'underline' }}
                    >
                      {row.delivery_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Delivery: {row.delivery_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDateTime(row.completed_at)}</TableCell>
                  <TableCell>{row.waste_type}</TableCell>
                  <TableCell align="right">
                    {row.amount.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell>{row.recycler_name ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default DriverDetail;
